
import { processEmoji, isValidEmoji } from "@/utils/emojiUtils";

interface MessageSticker {
  id: string;
  emoji: string;
  userId: string;
  userName: string;
  count: number;
}

interface MessageStickersProps {
  messageId: string;
  stickers: MessageSticker[];
  currentUserId?: string;
  onRemoveSticker?: (messageId: string, emoji: string) => void;
  onAddSticker?: (messageId: string, emoji: string) => void;
  isMediaMessage?: boolean;
}

export default function MessageStickers({ 
  messageId, 
  stickers, 
  currentUserId,
  onRemoveSticker,
  onAddSticker,
  isMediaMessage = false
}: MessageStickersProps) {
  console.log("🎪 MessageStickers RENDER:", { 
    messageId: messageId?.substring(0, 8), 
    stickersCount: stickers?.length || 0, 
    currentUserId: currentUserId?.substring(0, 8), 
    hasRemoveHandler: !!onRemoveSticker,
    hasAddHandler: !!onAddSticker,
    isMediaMessage 
  });
  
  if (!stickers || stickers.length === 0) {
    console.log("❌ No stickers to display for message:", messageId?.substring(0, 8));
    return null;
  }

  // Enhanced validation and filtering with better error handling
  const validStickers = stickers.filter(sticker => {
    if (!sticker || !sticker.emoji) {
      console.error("Sticker missing emoji data:", sticker);
      return false;
    }

    const isValid = isValidEmoji(sticker.emoji);
    if (!isValid) {
      console.error("Invalid sticker emoji detected, filtering out:", {
        id: sticker.id,
        emoji: sticker.emoji,
        emojiLength: sticker.emoji?.length,
        emojiType: typeof sticker.emoji
      });
    }
    return isValid;
  });

  if (validStickers.length === 0) {
    console.log("No valid stickers to display for message:", messageId);
    return null;
  }

  // Group stickers by emoji with enhanced validation and deduplication
  const groupedStickers = validStickers.reduce((acc, sticker) => {
    const processedEmoji = processEmoji(sticker.emoji, 'MessageStickers grouping');
    console.log("Processing sticker emoji:", { 
      original: sticker.emoji, 
      processed: processedEmoji,
      isValid: processedEmoji !== '❓'
    });
    
    // Skip fallback emojis to prevent displaying question marks
    if (processedEmoji === '❓') {
      console.warn("Skipping fallback emoji for sticker:", sticker.id);
      return acc;
    }
    
    if (!acc[processedEmoji]) {
      acc[processedEmoji] = {
        emoji: processedEmoji,
        count: 0,
        users: [],
        hasCurrentUser: false
      };
    }
    acc[processedEmoji].count += sticker.count;
    if (!acc[processedEmoji].users.includes(sticker.userName)) {
      acc[processedEmoji].users.push(sticker.userName);
    }
    if (sticker.userId === currentUserId) {
      acc[processedEmoji].hasCurrentUser = true;
    }
    return acc;
  }, {} as Record<string, { emoji: string; count: number; users: string[]; hasCurrentUser: boolean }>);

  console.log("Grouped stickers:", groupedStickers);

  // Only render if we have valid grouped stickers
  if (Object.keys(groupedStickers).length === 0) {
    console.log("No valid grouped stickers to display");
    return null;
  }

  const handleStickerClick = (event: React.MouseEvent, emoji: string, hasCurrentUser: boolean) => {
    // Prevent event bubbling to avoid triggering timestamp display
    event.preventDefault();
    event.stopPropagation();
    
    console.log("🎯 STICKER CLICKED!", { emoji, hasCurrentUser, messageId, currentUserId });
    
    // Validate parameters before calling handlers
    if (!messageId || !emoji) {
      console.error("Invalid parameters for sticker click:", { messageId, emoji });
      return;
    }

    // Safety check to prevent corruption
    if (messageId === emoji) {
      console.error("MessageId and emoji are identical, preventing corruption");
      return;
    }

    // Additional validation to ensure we have a valid emoji
    if (!isValidEmoji(emoji)) {
      console.error("Attempted to interact with invalid emoji:", emoji);
      return;
    }

    if (hasCurrentUser && onRemoveSticker) {
      console.log("🔴 CALLING onRemoveSticker:", { messageId, emoji });
      onRemoveSticker(messageId, emoji);
    } else if (!hasCurrentUser && onAddSticker) {
      console.log("🟢 CALLING onAddSticker:", { messageId, emoji });
      onAddSticker(messageId, emoji);
    }
  };

  return (
    <div className={`absolute z-30 ${
      isMediaMessage 
        ? "bottom-2 left-2" // Position at bottom-left for media
        : "-bottom-5 left-0 right-0 flex justify-center" // Use freed timestamp space
    }`}>
      <div className={`flex flex-row gap-1 items-center ${
        isMediaMessage ? "max-w-[200px]" : "max-w-[280px]"
      }`}>
        {Object.values(groupedStickers).map((group, index) => {
          // Only show count if there are multiple users or multiple reactions of the same emoji
          const showCount = group.count > 1;
          
          return (
            <span
              key={`${messageId}-${group.emoji}-${index}`}
              className={`inline-flex items-center cursor-pointer transition-all duration-200 hover:scale-110 ${
                isMediaMessage ? "text-lg" : "text-xl"
              } ${showCount ? "gap-0.5" : ""}`}
              onClick={(event) => {
                console.log("🎯 SPAN CLICKED! About to call handleStickerClick");
                handleStickerClick(event, group.emoji, group.hasCurrentUser);
              }}
              title={`${group.users.join(", ")} reacted with ${group.emoji}${group.hasCurrentUser ? ' - Click to remove your reaction' : ' - Click to add your reaction'}`}
            >
              <span 
                className="select-none leading-none"
                style={{ 
                  fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif',
                  fontSize: isMediaMessage ? '16px' : '20px',
                  lineHeight: '1',
                  display: 'inline-block'
                }}
              >
                {group.emoji}
              </span>
              {showCount && (
                <span className="text-xs font-semibold leading-none text-foreground/70 ml-0.5">
                  {group.count}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
